import type { Logger } from '@moojo/logger'

import type { EventTracker } from '../analytics/event-tracker'
import { AdExtension } from '../vf-extensions/ad-extension'
import { CouponExtension, makeCouponExtension } from '../vf-extensions/coupon-extension'
import { getVisibilityPercentage } from './check-element-visibility'
import type { DOMUtils } from './dom-utils'
import { DomWatcher } from './dom-watcher'
import { Hooks } from './hooks'
import type { AttributeChangeInfo } from './on-element-attribute-change'
import { onElementAttributeChange } from './on-element-attribute-change'
import { windowVoiceflowChat } from './window-voiceflow-chat'

export type VoiceflowAppearanceSettings = Partial<{
  side: 'left' | 'right'
  launcherIconUrl: string
  launcherIconBackgroundColor: string
  launcherFontColor: string
  launcherBackgroundColor: string
  headerIconUrl: string
  headerIconBackgroundColor: string
  headerFontColor: string
  headerBackgroundColor: string
  avatarIconUrl: string
  avatarIconBackgroundColor: string
  fontColor: string
  quickActionBackgroundColor: string
  quickActionFontColor: string
}>

export type VoiceflowConfig = {
  projectId: string
  persistence: 'localStorage' | 'sessionStorage' | 'memory'
  launcherText: string
  placeholderText: string
  headerText: string
  appearance?: VoiceflowAppearanceSettings
}

const VF_SCRIPT_URL = 'https://cdn.voiceflow.com/widget-next/bundle.mjs'
const VF_RUNTIME_URL = 'https://general-runtime.voiceflow.com'
const VF_VOICE_URL = 'https://runtime-api.voiceflow.com'

export class VoiceflowService {
  constructor(
    private readonly loadPromise: Promise<void>,
    private readonly agent: 'dev' | 'prod',
    private readonly eventTracker: EventTracker,
  ) {}

  static async load(
    logger: Logger,
    eventTracker: EventTracker,
    domUtils: DOMUtils,
    agent: 'dev' | 'prod',
  ): Promise<VoiceflowService> {
    const readinessReason = await domUtils.waitForDomReady()
    logger.info(`Page ready for chat widget injection. readinessReason: ${readinessReason}`)

    Hooks.setupFetchHook()

    const loadPromise = new Promise<void>((resolve, reject) => {
      const script = domUtils.createScript(VF_SCRIPT_URL, {
        async: '',
      })

      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Voiceflow script'))

      document.head.appendChild(script)
    })

    return new VoiceflowService(loadPromise, agent, eventTracker)
  }

  async show(config: VoiceflowConfig, combinedArgs: Record<string, unknown>) {
    await this.loadPromise

    // make sure chat's shadow root host is always visible.
    const style = document.createElement('style')
    style.textContent = '#voiceflow-chat { display: block !important; }'
    document.head.appendChild(style)

    await this.initializeVoiceflow(config, combinedArgs, this.agent)
  }

  async open() {
    windowVoiceflowChat(false).open()
  }

  changeAppearance(settings: VoiceflowAppearanceSettings) {
    const vfRoot = windowVoiceflowChat()._shadowRoot as DocumentFragment

    const newStyle = document.createElement('style')
    newStyle.textContent = makeInlineStylesheet(settings)

    const widget = vfRoot.querySelector('.vfrc-widget')
    vfRoot.insertBefore(newStyle, widget ?? null)
    // TODO: roman: consider removing last link/css element
  }

  destroy() {
    windowVoiceflowChat(false).destroy()
  }

  // https://docs.voiceflow.com/docs/advanced-styling
  private async initializeVoiceflow(
    config: VoiceflowConfig,
    combinedArgs: Record<string, unknown>,
    agent: 'dev' | 'prod',
  ) {
    const extraStyles =
      combinedArgs['brandName'] === 'SIAMO NOI'
        ? [
            // TODO(roman): make this configurable.
            // atm, using hardcoded color for user response for Siamo Noi agent, as agreed in this thread:
            // https://moojo-hq.slack.com/archives/C08UK8JMGQ1/p1751971727732319?thread_ts=1751970211.957989&cid=C08UK8JMGQ1
            cssDirective('.vfrc-user-response div', ['color', '#644F45']),
          ]
        : []

    const stylesheet = makeInlineStylesheet(config.appearance, extraStyles).trim()

    const vfConfig = {
      verify: { projectID: config.projectId },
      url: VF_RUNTIME_URL,
      versionID: agent === 'dev' ? 'development' : 'production',
      // TODO(roman): when user info becomes relevant
      // user: {
      //   userID: 'custom-user-123',
      //   name: 'John Doe',
      //   email: 'john@example.com',
      // },
      voice: {
        url: VF_VOICE_URL,
      },
      assistant: {
        withClose: true,
        persistence: config.persistence,
        side: config.appearance?.side ?? 'right',
        inputPlaceholder: config.placeholderText,
        header: {
          hideImage: config.appearance?.headerIconUrl === undefined,
          imageUrl: config.appearance?.headerIconUrl, // goes to header as well
        },
        banner: {
          hide: true,
          title: config.headerText,
        },
        avatar: {
          hide: config.appearance?.avatarIconUrl !== undefined,
          imageUrl: config.appearance?.avatarIconUrl,
        },
        launcher: {
          type: config.appearance?.launcherIconUrl ? 'both' : 'label', // icon, label, both
          label: config.launcherText,
          imageUrl: config.appearance?.launcherIconUrl,
        },
        stylesheet: makeStylesheetData(stylesheet),
        extensions: [AdExtension, makeCouponExtension(this.eventTracker)],
      },
      launch: {
        event: {
          type: 'launch',
          payload: combinedArgs,
        },
      },
    }

    const chat = windowVoiceflowChat()
    await chat.load(vfConfig)

    this.applyPostLoadSideEffects(chat)
  }

  private applyPostLoadSideEffects(chat: ReturnType<typeof windowVoiceflowChat>) {
    const watcher = new DomWatcher({ root: chat._shadowRoot, timeout: 5000 })

    this.setupCloseButton(watcher, chat)
    this.disableAutofocus(watcher)
    this.trackLauncherVisibility(watcher, chat)
  }

  private setupCloseButton(watcher: DomWatcher, chat: ReturnType<typeof windowVoiceflowChat>) {
    watcher.onElementAddedOnce('.vfrc-header', (header: Element) => {
      const buttonWrapper = header.ownerDocument.createElement('div')
      // voiceflow's close button is not visible on NON mobile devices, hence we need to add it manually.
      // the next html snippet is copied from the original VF code.
      // TODO(roman): find a better way to do this.
      buttonWrapper.innerHTML = `<button title="Chat" class="vfrc-button _1q15b0b0 c26d8x0 _1q15b0b2 vfrc-header--button-unhide jyiueib c26d8x0 jyiueid"><div class="vfrc-icon _1t7e06e0"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M17.7478 7.31915C18.0496 7.01737 18.0496 6.5281 17.7478 6.22633C17.4461 5.92456 16.9568 5.92456 16.655 6.22633L12.2663 10.6151C12.1244 10.757 11.8943 10.757 11.7524 10.6151L7.36785 6.23053C7.06376 5.92644 6.57073 5.92644 6.26664 6.23053L6.22807 6.2691C5.92398 6.57319 5.92398 7.06622 6.22807 7.37032L10.6126 11.7548C10.7545 11.8968 10.7545 12.1268 10.6126 12.2688L6.24255 16.6388C5.94078 16.9406 5.94078 17.4298 6.24255 17.7316C6.54433 18.0334 7.0336 18.0334 7.33537 17.7316L11.7054 13.3616C11.8473 13.2197 12.0774 13.2197 12.2193 13.3616L16.6321 17.7744C16.9362 18.0785 17.4293 18.0785 17.7334 17.7744L17.7719 17.7358C18.076 17.4317 18.076 16.9387 17.7719 16.6346L13.3591 12.2218C13.2172 12.0799 13.2172 11.8498 13.3591 11.7079L17.7478 7.31915Z" fill="currentColor"></path></svg></div></button>`

      const closeBtn = buttonWrapper.firstElementChild
      if (closeBtn) {
        const buttonContainer =
          header.querySelector('button')?.parentElement ?? header.firstElementChild?.lastElementChild

        closeBtn.addEventListener('click', () => chat.close())
        buttonContainer?.appendChild(closeBtn)
      }
      // else - not supposed to happen.
    })
  }

  private disableAutofocus(watcher: DomWatcher) {
    watcher.onElementAddedOnce('textarea.vfrc-chat-input', (inputTextarea: Element) => {
      const removeAutofocus = () => {
        inputTextarea.removeAttribute('autofocus')
        ;(inputTextarea as HTMLTextAreaElement).blur()
      }

      removeAutofocus()

      // Important: VF sets autofocus again after launcher clicked and chat shows.

      onElementAttributeChange(
        inputTextarea,
        (info: AttributeChangeInfo) => {
          if (info.attributeName === 'autofocus') {
            removeAutofocus()
          }
        },
        {
          // no timeout, not once - we want to remove autofocus every time it was set.
          attributes: ['autofocus'],
        },
      )
    })
  }

  private trackLauncherVisibility(watcher: DomWatcher, chat: ReturnType<typeof windowVoiceflowChat>) {
    let eventSent = false

    const cleanup = watcher.onElementAddedOnce('.vfrc-widget button', (launcher: Element) => {
      const percentage = getVisibilityPercentage(launcher)
      this.eventTracker.track('launcher-visibility-test', {
        found: 'true',
        visibilityPercentage: String(percentage),
      })
      eventSent = true
    })

    setTimeout(() => {
      cleanup()
      if (eventSent) {
        return
      }

      const launcher = (chat._shadowRoot as DocumentFragment).querySelector('.vfrc-widget button')
      if (!launcher) {
        this.eventTracker.track('launcher-visibility-test', {
          found: 'false',
          reason: 'timeout',
        })
      }
    }, 5000)
  }
}

const makeInlineStylesheet = (appearance: VoiceflowAppearanceSettings | undefined, extraStyles: string[] = []) =>
  [
    ...(appearance !== undefined
      ? [
          cssDirective(
            '.vfrc-launcher',
            ['color', appearance.launcherFontColor],
            ['background-color', appearance.launcherBackgroundColor],
          ),
          cssDirective('.vfrc-launcher img', ['background-color', appearance.launcherIconBackgroundColor]),
          cssDirective('.vfrc-header', ['background-color', appearance.headerBackgroundColor]),
          cssDirective('.vfrc-header div', ['color', appearance.headerFontColor]), // note: header text doesn't have explicit class name.
          cssDirective('.vfrc-chat--dialog p', ['color', appearance.fontColor]),
          cssDirective('.vfrc-avatar', ['background-color', appearance.avatarIconBackgroundColor]),
          cssDirective(
            '.vfrc-system-response--actions button',
            ['color', appearance.quickActionFontColor],
            ['background-color', appearance.quickActionBackgroundColor],
          ),
          cssDirective('.vfrc-user-response div', ['background-color', appearance.quickActionBackgroundColor]),
        ]
      : []),
    // keep the padding the same from all directions (is not by VF default).
    // https://linear.app/tant/issue/ENG-214/the-gray-frame-should-be-consistent-around-the-ad

    cssDirective(`.vfrc-message--extension-${AdExtension.name}`, ['padding', '8px']),
    cssDirective(`.vfrc-message--extension-${CouponExtension.name}`, ['padding', '8px']),
    // header buttons - there are 3 possible buttons: reset, close and voice
    // there is no way to distinguish between the buttons vy css selector.
    // the only button we care to keep is 'close'. On desktop, it's omitted and we need to
    // add it manually (see observer code above).
    cssDirective('.vfrc-header .vfrc-header--button', ['display', 'none']),
    ...extraStyles,
  ].join('\n')

const cssDirective = (selector: string, ...kvp: [string, string | undefined][]) => {
  const filtered = kvp.filter((kv): kv is [string, string] => kv[1] !== undefined)
  if (filtered.length === 0) {
    return ''
  }

  return `${selector} {${filtered.map(([k, v]) => `${k}: ${v} !important;`).join(' ')}}`
}

const makeStylesheetData = (stylesheet: string) => 'data:text/css;base64,' + btoa(stylesheet)
